import type { RunbookSummarizeRequest } from '@dev-console/types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { API_BASE_URL } from '@/config/api';

interface UseRunbookStreamReturn {
  content: string;
  isStreaming: boolean;
  error: string;
  startStream: (request: RunbookSummarizeRequest) => void;
  reset: () => void;
}

export function useRunbookStream(): UseRunbookStreamReturn {
  const [content, setContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  // Abort any in-flight stream on unmount
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setContent('');
    setIsStreaming(false);
    setError('');
  }, []);

  const startStream = useCallback((request: RunbookSummarizeRequest) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setContent('');
    setIsStreaming(true);
    setError('');

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/dev-console/runbook/summarize`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
          signal: controller.signal,
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';
        let reading = true;

        while (reading) {
          const { done, value } = await reader.read();
          if (done) {
            reading = false;
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const payload = line.slice(6);
            if (payload === '[DONE]') continue;

            try {
              const parsed = JSON.parse(payload) as { content?: string; error?: string };
              if (parsed.error) {
                setError(parsed.error);
              } else if (parsed.content) {
                setContent((prev) => prev + parsed.content);
              }
            } catch {
              // Skip malformed SSE lines
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError((err as Error).message);
        }
      } finally {
        setIsStreaming(false);
      }
    })();
  }, []);

  return { content, isStreaming, error, startStream, reset };
}
